<script lang="ts" setup>
import { ref, computed, onBeforeMount } from "vue";
import { useRoute } from "vue-router";
import CaikumaTeacher from "@/assets/svg/UnderstandingTest/CaikumaTeacher.svg?component";
import Step1 from "@/assets/svg/UnderstandingTest/Step1.svg?component";
import Step2 from "@/assets/svg/UnderstandingTest/Step2.svg?component";
import Step3 from "@/assets/svg/UnderstandingTest/Step3.svg?component";
import Correct from "@/assets/svg/UnderstandingTest/Correct.svg?component";
import Incorrect from "@/assets/svg/UnderstandingTest/Incorrect.svg?component";
import AppButton from "@/components/atoms/button/AppButton.vue";
import { useCustomRouter } from "@/utils/router";

type PageType = "question" | "answer";
type Answer = "YES" | "NO";
type Question = {
  question: string;
  answer: Answer;
  explanation: {
    highlight: string;
    normal: string;
  };
};

const allQuestions: Question[] = [
  {
    question: "お仕事申込み完了後、事業所側から連絡があり、面接が行われる",
    answer: "NO",
    explanation: {
      highlight: "申込み完了後は、その時点で「確定」となります",
      normal: "申込み前に内容をよく確認してから、申込みましょう！"
    }
  },
  {
    question: "無断欠勤をすると、一時的に利用停止になる",
    answer: "YES",
    explanation: {
      highlight:
        "いかなる理由があっても、無断欠勤をすると「一時的に利用停止」となります",
      normal: "出勤できなくなった時点で、必ずキャンセルしましょう！"
    }
  },
  {
    question:
      "「当日」と「前日」のキャンセルでは、信用スコアの減る点数は同じである",
    answer: "NO",
    explanation: {
      highlight: "前日より当日キャンセルの方が、信用スコアは大きく減点されます",
      normal:
        "また、直前キャンセルが多いと応募できるお仕事が限定されてしまいます。"
    }
  },
  {
    question: "キャンセル申請は取り消すことができない",
    answer: "YES",
    explanation: {
      highlight:
        "一度アプリよりキャンセルの申請をすると、取り消すことができません",
      normal: "キャンセル申請される際は、注意しましょう！"
    }
  },
  {
    question: "勤務開始時間を過ぎると、アプリよりキャンセル申請ができない",
    answer: "YES",
    explanation: {
      highlight: "勤務開始時間を過ぎるとアプリよりキャンセル申請ができません",
      normal:
        "必ず勤務開始前までにアプリからキャンセル申請をしましょう！電話連絡だけでは、キャンセル未申請のままです。事務局での手続き完了まで画面操作ができなくなります。"
    }
  },
  {
    question: "応募条件を満たしていないと、キャンセルになる場合がある",
    answer: "YES",
    explanation: {
      highlight: "応募条件は事業所ごとに異なります",
      normal: "必ず仕事詳細の応募条件を確認し、応募するようにしましょう！"
    }
  },
  {
    question: "キャンセルにより信用スコアが0ptになった場合、永久に回復されない",
    answer: "NO",
    explanation: {
      highlight: "信用スコア0ptになった場合、一定期間を経て回復します",
      normal: "詳細はFAQをご確認ください。"
    }
  },
  {
    question:
      "勤務日前日または当日のお仕事のキャンセルの場合でも事業所にメッセージで伝えれば良い",
    answer: "NO",
    explanation: {
      highlight:
        "勤務日前日または当日のキャンセルは、事業所への電話連絡が必須です",
      normal: "電話連絡とアプリ上の申請を実施しましょう！"
    }
  },
  {
    question: "お仕事確定後に事業所と事前連絡ができる",
    answer: "YES",
    explanation: {
      highlight: "応募先の事業所とメッセージにて連絡することができます",
      normal:
        "事業所からのメッセージ受信時にはプッシュ通知にてお知らせいたします。"
    }
  }
];

const route = useRoute();
const { replaceIgnoreError } = useCustomRouter();
const targetQuestions = ref<Question[]>([]);
const currentStep = ref(1);
const currentPageType = ref<PageType>("question");
const currentAnswer = ref<Answer | null>(null);
const isCorrect = ref<boolean | null>(null);

const orderId = computed(() => Number(route.params["orderId"]));
const workTimeOptionId = computed(() =>
  Number(route.params["workTimeOptionId"])
);

const currentQuestion = computed(
  () => targetQuestions.value[currentStep.value - 1]
);
const showsClickNextButton = computed(() => {
  return currentPageType.value === "answer" && isCorrect.value === true;
});
const showsBackToQuestionButton = computed(() => {
  return currentPageType.value === "answer" && isCorrect.value === false;
});
const showsYesNoButtons = computed(() => {
  return currentPageType.value === "question";
});

onBeforeMount(() => {
  targetQuestions.value = pickUpQuestions();
});

// ランダムに3問取得
function pickUpQuestions() {
  return allQuestions.sort(() => Math.random() - 0.5).slice(0, 3);
}

async function onClickNext() {
  if (currentStep.value < 3) {
    currentStep.value = currentStep.value + 1;
    currentPageType.value = "question";
    currentAnswer.value = null;
    isCorrect.value = null;
    window.scrollTo(0, 0);
  } else {
    await replaceIgnoreError({
      name: "OrdersUnderstandingTestFinish",
      params: {
        orderId: orderId.value.toString(),
        workTimeOptionId: workTimeOptionId.value.toString()
      }
    });
  }
}

function onClickBackToQuestion() {
  currentPageType.value = "question";
  currentAnswer.value = null;
  isCorrect.value = null;
}

function onClickYesNo(answer: Answer) {
  currentPageType.value = "answer";
  currentAnswer.value = answer;
  isCorrect.value = currentQuestion.value.answer === answer;
  window.scrollTo(0, 0);
}
</script>

<template>
  <div class="container">
    <div class="header">
      理解度テスト
    </div>
    <div class="progress">
      <div class="bar">
        <template v-if="currentStep === 1"><Step1 /></template>
        <template v-else-if="currentStep === 2"><Step2 /></template>
        <template v-else-if="currentStep === 3"><Step3 /></template>
      </div>
      <div class="step">第{{ currentStep }}問</div>
    </div>
    <div class="content">
      <div v-if="currentPageType === 'question'" class="question-container">
        <div class="question">
          {{ currentQuestion.question }}
        </div>
        <div class="teacher-image"><CaikumaTeacher /></div>
      </div>
      <div v-else class="answer-container">
        <div class="question">
          {{ currentQuestion.question }}
        </div>
        <div class="answer">
          {{ currentAnswer }}
        </div>
        <div class="result">
          <div class="correctness">
            <template v-if="isCorrect">
              <img
                src="@/assets/svg/UnderstandingTest/CaikumaElated.svg?webp"
                class="image"
              />
              <div>
                <div class="title">
                  <Correct />
                  <span class="label correct">正解</span>
                </div>
                <div>おめでとうございます！</div>
              </div>
            </template>
            <template v-else>
              <div class="image">
                <img
                  src="@/assets/svg/UnderstandingTest/CaikumaSad.svg?webp"
                  alt="CaikumaSad"
                />
              </div>
              <div>
                <div class="title">
                  <Incorrect />
                  <span class="label incorrect">不正解</span>
                </div>
                <div>もう一回答えてください</div>
              </div>
            </template>
          </div>
          <div class="explanation">
            <div class="title">【解説】</div>
            <div class="highlight">
              {{ currentQuestion.explanation.highlight }}
            </div>
            <div class="normal">{{ currentQuestion.explanation.normal }}</div>
          </div>
        </div>
      </div>
    </div>
    <div class="footer bg-white">
      <AppButton
        v-if="showsClickNextButton"
        block
        class="button"
        @click="onClickNext"
        >次の画面へ</AppButton
      >
      <AppButton
        v-if="showsBackToQuestionButton"
        block
        class="button"
        @click="onClickBackToQuestion"
        >問題へ戻る</AppButton
      >
      <template v-if="showsYesNoButtons">
        <AppButton block class="button" @click="onClickYesNo('YES')"
          >YES</AppButton
        >
        <AppButton block class="button" @click="onClickYesNo('NO')"
          >NO</AppButton
        >
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.container {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  min-height: 100vh;
  padding: env(safe-area-inset-top, 0) 20px 0;
  background-color: white;

  .header {
    margin-top: 40px;
    margin-bottom: 30px;
    font-size: 20px;
    font-weight: 600;
    line-height: 26px;
    text-align: center;
    letter-spacing: 0;
  }

  .progress {
    display: block;
    height: 90px;
    background-color: white;

    .bar {
      display: flex;
      justify-content: center;
    }

    .step {
      width: 100%;
      margin-top: 20px;
      font-weight: 600;
      line-height: 24px;
      text-align: center;
      letter-spacing: 0;
    }
  }

  .content {
    margin-bottom: 80px;

    .question-container {
      .question {
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 16px;
        text-align: center;
        border: 1px solid #e5e5e5;
        border-radius: 4px;
        @media only screen and (max-height: 800px) {
          height: 150px;
        }

        @media only screen and (min-height: 801px) {
          height: 300px;
        }
      }

      .teacher-image {
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
          @media only screen and (max-height: 800px) {
            width: 90px;
            height: 93px;
            margin: 30px;
          }

          @media only screen and (min-height: 801px) {
            width: 120px;
            height: 124px;
            margin: 50px;
          }
        }
      }
    }

    .answer-container {
      .question {
        display: flex;
        flex-direction: column;
        justify-content: center;
        height: 80px;
        padding: 16px;
        text-align: center;
        border: 1px solid #e5e5e5;
        border-radius: 4px 4px 0 0;
      }

      .answer {
        display: flex;
        flex-direction: column;
        justify-content: center;
        height: 40px;
        padding: 8px 0;
        text-align: center;
        background-color: #f5f5f5;
        border: 1px solid #e5e5e5;
        border-radius: 0 0 4px 4px;
      }

      .result {
        margin: 10px 10px 30px;

        .correctness {
          display: flex;
          justify-content: flex-start;
          margin-bottom: 20px;

          .image {
            width: 120px;
            height: 124px;
            text-align: center;
          }

          .title {
            margin: 10px 0;

            .label {
              margin-left: 8px;
              font-size: 24px;
              font-weight: 600;
              vertical-align: middle;

              &.correct {
                color: #f4a722;
              }

              &.incorrect {
                color: #c2493e;
              }
            }
          }
        }

        .explanation {
          .title {
            margin-bottom: 5px;
            font-weight: 600;
          }

          .highlight {
            margin-bottom: 5px;
            font-weight: 600;
            color: #c2493e;
          }
        }
      }
    }
  }

  .footer {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    width: 100%;
    padding: 8px 16px 28px;

    .button + .button {
      margin-top: 0;
      margin-left: 20px;
    }
  }
}
</style>
